import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import 'moment/locale/ja';

moment.locale('ja');

const ADMIN_ATTENDANCE_API = 'https://system.sales-management.cloud/api/admin/attendance.php';
const USERS_API = 'https://system.sales-management.cloud/api/users.php';

function Attendance() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [showProxyModal, setShowProxyModal] = useState(false);
  const [proxyForm, setProxyForm] = useState({
    user_id: '',
    clock_in: '',
    clock_out: '',
    breaks: []
  });
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailImages, setDetailImages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [stampRecords, setStampRecords] = useState([]);
  const [stampLoading, setStampLoading] = useState(false);
  
  // 月次ビュー用のstate
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
  const [selectedUserId, setSelectedUserId] = useState('');
  const [monthlyRecords, setMonthlyRecords] = useState([]);

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfo = Cookies.get('user_info');
    const user = userInfo ? JSON.parse(userInfo) : null;
    return { token, user };
  }, []);

  // 打刻データ取得
  const fetchAttendance = useCallback(async (date = selectedDate) => {
    setLoading(true);
    setError('');

    try {
      const { token } = getAuthData();

      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const dateObj = moment(date);
      const dateFrom = dateObj.format('YYYY-MM-DD');
      const dateTo = dateObj.format('YYYY-MM-DD');

      const response = await fetch(
        `${ADMIN_ATTENDANCE_API}?action=list&date_from=${dateFrom}&date_to=${dateTo}&limit=500`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      console.log('API Response:', data);

      if (data.status === 'success') {
        const records = data.data?.records || [];
        console.log('Fetched records:', records);
        setAttendanceRecords(records);
      } else {
        setError(data.message || '打刻データの取得に失敗しました');
        setAttendanceRecords([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, getAuthData]);

  // 月次データ取得
  const fetchMonthlyAttendance = useCallback(async (month = selectedMonth, userId = selectedUserId) => {
    if (!userId) {
      setError('ユーザーを選択してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { token } = getAuthData();

      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const monthObj = moment(month);
      const dateFrom = monthObj.startOf('month').format('YYYY-MM-DD');
      const dateTo = monthObj.endOf('month').format('YYYY-MM-DD');

      const response = await fetch(
        `${ADMIN_ATTENDANCE_API}?action=list&date_from=${dateFrom}&date_to=${dateTo}&user_id=${userId}&limit=500`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();

      console.log('Monthly API Response:', data);

      if (data.status === 'success') {
        const records = data.data?.records || [];
        console.log('Fetched monthly records:', records);
        
        // 重複チェック: 同じIDが複数ないか確認
        const ids = records.map(r => r.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error('⚠️ 重複したIDが検出されました!');
          console.error('総レコード数:', ids.length, '/ ユニークなID数:', uniqueIds.size);
          console.error('重複しているID:', ids.filter((id, idx) => ids.indexOf(id) !== idx));
          
          // 重複を除去
          const uniqueRecords = [];
          const seenIds = new Set();
          records.forEach(record => {
            if (!seenIds.has(record.id)) {
              seenIds.add(record.id);
              uniqueRecords.push(record);
            }
          });
          console.log('重複除去後:', uniqueRecords.length, 'レコード');
          setMonthlyRecords(uniqueRecords);
        } else {
          setMonthlyRecords(records);
        }
      } else {
        setError(data.message || '月次データの取得に失敗しました');
        setMonthlyRecords([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedUserId, getAuthData]);

  // CSVエクスポート
  const exportCsv = useCallback(async ({ type = 'attendance', startDate, endDate, userId }) => {
    try {
      const { token } = getAuthData();
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const params = new URLSearchParams();
      params.set('action', 'export');
      params.set('type', type);
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      if (userId) params.set('user_id', userId);

      const url = `${ADMIN_ATTENDANCE_API}?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'CSVの取得に失敗しました');
      }

      const csvText = await response.text();
      const bom = '\ufeff';
      const csvWithBom = csvText.charCodeAt(0) === 0xfeff ? csvText : `${bom}${csvText}`;
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8' });

      // ファイル名をヘッダーから取得（なければデフォルト名）
      const disposition = response.headers.get('Content-Disposition') || '';
      let filename = 'export.csv';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        filename = decodeURIComponent(match[1]);
      } else {
        const base = type === 'reports' ? 'daily_reports' : 'attendance';
        filename = `${base}_${startDate}_to_${endDate}.csv`;
      }

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      setSuccess('CSVをダウンロードしました');
    } catch (err) {
      console.error('Export error:', err);
      setError(typeof err?.message === 'string' ? err.message : 'CSVエクスポートでエラーが発生しました');
    }
  }, [getAuthData]);

  // ユーザー一覧取得
  const fetchUsers = useCallback(async () => {
    try {
      const { token } = getAuthData();

      if (!token) {
        setUsers([]);
        return;
      }

      const response = await fetch(USERS_API, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.status === 'success') {
        const userList = data.data.users || [];
        // オブジェクトが返された場合は配列に変換
        const userArray = Array.isArray(userList) ? userList : Object.values(userList);
        console.log('Fetched users:', userArray);
        setUsers(userArray);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setUsers([]);
    }
  }, [getAuthData]);

  useEffect(() => {
    fetchUsers();
    fetchAttendance();
  }, [fetchUsers, fetchAttendance]);

  // 代理打刻登録
  const handleProxyAttendance = useCallback(async () => {
    if (!proxyForm.user_id) {
      setError('従業員を選択してください');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();

      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const proxyBody = {
        action: 'proxy_record',
        user_id: proxyForm.user_id,
        work_date: selectedDate,
        breaks: (proxyForm.breaks || [])
          .filter((entry) => entry.start || entry.end)
          .map((entry) => ({
            start: entry.start || '',
            end: entry.end || ''
          }))
      };

      if (proxyForm.clock_in) {
        proxyBody.clock_in = proxyForm.clock_in.length === 5 ? proxyForm.clock_in + ':00' : proxyForm.clock_in;
      }
      if (proxyForm.clock_out) {
        proxyBody.clock_out = proxyForm.clock_out.length === 5 ? proxyForm.clock_out + ':00' : proxyForm.clock_out;
      }

      const response = await fetch(ADMIN_ATTENDANCE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(proxyBody)
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccess('代理打刻を登録しました');
        setShowProxyModal(false);
        setProxyForm({ user_id: '', clock_in: '', clock_out: '', breaks: [] });
        fetchAttendance();
      } else {
        setError(data.message || '代理打刻の登録に失敗しました');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [proxyForm, selectedDate, getAuthData, fetchAttendance]);

  const fetchStampHistory = useCallback(async (userId, workDate) => {
    if (!userId || !workDate) {
      setStampRecords([]);
      return;
    }

    setStampLoading(true);
    try {
      const { token } = getAuthData();
      if (!token) return;

      const params = new URLSearchParams({
        action: 'stamps',
        user_id: String(userId),
        work_date: workDate,
      });

      const response = await fetch(`${ADMIN_ATTENDANCE_API}?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.status === 'success') {
        setStampRecords(data.data?.stamps || []);
      } else {
        setError(data.message || '打刻履歴の取得に失敗しました');
        setStampRecords([]);
      }
    } catch (err) {
      console.error('Fetch stamps error:', err);
      setError('打刻履歴の取得で通信エラーが発生しました');
      setStampRecords([]);
    } finally {
      setStampLoading(false);
    }
  }, [getAuthData]);

  // 詳細情報取得
  const fetchAttendanceDetail = useCallback(async (recordId) => {
    setDetailLoading(true);
    try {
      const { token } = getAuthData();
      if (!token) return;

      const response = await fetch(
        `${ADMIN_ATTENDANCE_API}?action=detail&id=${recordId}`,
        {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.status === 'success') {
        const record = data.data?.record || null;
        setDetailRecord(record);
        setDetailImages(data.data?.images || []);
        console.log('Detail record:', record);
        if (record?.user_id && record?.work_date) {
          fetchStampHistory(record.user_id, record.work_date);
        } else {
          setStampRecords([]);
        }
        setShowDetailModal(true);
        // 一覧を更新
        fetchAttendance();
      } else {
        setError(data.message || '詳細情報の取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setDetailLoading(false);
    }
  }, [getAuthData, fetchAttendance, fetchStampHistory]);

  const refreshCurrentView = useCallback(() => {
    if (viewMode === 'daily') {
      fetchAttendance(selectedDate);
      return;
    }
    if (selectedUserId) {
      fetchMonthlyAttendance(selectedMonth, selectedUserId);
    }
  }, [viewMode, selectedDate, selectedMonth, selectedUserId, fetchAttendance, fetchMonthlyAttendance]);

  const handleCreateStamp = useCallback(async ({ user_id, work_date, type, time, memo }) => {
    if (!user_id || !work_date || !type || !time) {
      setError('打刻履歴の必須項目が不足しています');
      return;
    }

    setStampLoading(true);
    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) return;

      const response = await fetch(ADMIN_ATTENDANCE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'stamp_create',
          user_id,
          work_date,
          type,
          time,
          memo: memo || null
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('打刻履歴を追加しました');
        fetchStampHistory(user_id, work_date);
        refreshCurrentView();
      } else {
        setError(data.message || '打刻履歴の追加に失敗しました');
      }
    } catch (err) {
      console.error('Create stamp error:', err);
      setError('打刻履歴の追加で通信エラーが発生しました');
    } finally {
      setStampLoading(false);
    }
  }, [getAuthData, fetchStampHistory, refreshCurrentView]);

  const handleUpdateStamp = useCallback(async ({ id, user_id, work_date, type, time, memo }) => {
    if (!id || !type || !time) {
      setError('打刻履歴の更新に必要な情報が不足しています');
      return;
    }

    setStampLoading(true);
    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) return;

      const response = await fetch(ADMIN_ATTENDANCE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'stamp_update',
          id,
          work_date,
          type,
          time,
          memo: memo || null
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('打刻履歴を更新しました');
        if (user_id && work_date) {
          fetchStampHistory(user_id, work_date);
        }
        refreshCurrentView();
      } else {
        setError(data.message || '打刻履歴の更新に失敗しました');
      }
    } catch (err) {
      console.error('Update stamp error:', err);
      setError('打刻履歴の更新で通信エラーが発生しました');
    } finally {
      setStampLoading(false);
    }
  }, [getAuthData, fetchStampHistory, refreshCurrentView]);

  const handleDeleteStamp = useCallback(async ({ id, user_id, work_date }) => {
    if (!id) {
      setError('削除対象の打刻履歴が見つかりません');
      return;
    }

    setStampLoading(true);
    setError('');
    setSuccess('');

    try {
      const { token } = getAuthData();
      if (!token) return;

      const response = await fetch(ADMIN_ATTENDANCE_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'stamp_delete',
          id
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        setSuccess('打刻履歴を削除しました');
        if (user_id && work_date) {
          fetchStampHistory(user_id, work_date);
        }
        refreshCurrentView();
      } else {
        setError(data.message || '打刻履歴の削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete stamp error:', err);
      setError('打刻履歴の削除で通信エラーが発生しました');
    } finally {
      setStampLoading(false);
    }
  }, [getAuthData, fetchStampHistory, refreshCurrentView]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <Header />

      <MessageDisplay error={error} success={success} />

      {/* ビューモード切り替え */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setViewMode('daily')}
          className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
            viewMode === 'daily'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          日次ビュー
        </button>
        <button
          onClick={() => setViewMode('monthly')}
          className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
            viewMode === 'monthly'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          月次ビュー
        </button>
      </div>

      {/* 共通モーダル */}
      {showDetailModal && (
        <AttendanceDetailModal
          record={detailRecord}
          images={detailImages}
          stamps={stampRecords}
          stampLoading={stampLoading}
          onCreateStamp={handleCreateStamp}
          onUpdateStamp={handleUpdateStamp}
          onDeleteStamp={handleDeleteStamp}
          onClose={() => {
            setShowDetailModal(false);
            setDetailRecord(null);
            setDetailImages([]);
            setStampRecords([]);
          }}
          loading={detailLoading}
        />
      )}

      {showProxyModal && (
        <ProxyAttendanceModal
          users={users}
          proxyForm={proxyForm}
          onFormChange={setProxyForm}
          onSubmit={handleProxyAttendance}
          onClose={() => {
            setShowProxyModal(false);
            setProxyForm({ user_id: '', clock_in: '', clock_out: '', breaks: [] });
          }}
          loading={loading}
        />
      )}

      {viewMode === 'daily' ? (
        <>
          <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} onFetch={fetchAttendance} />

          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {moment(selectedDate).format('YYYY年M月D日')}の打刻データ
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowProxyModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                代理打刻
              </button>
              <button
                onClick={() => {
                  const d = moment(selectedDate).format('YYYY-MM-DD');
                  exportCsv({ type: 'attendance', startDate: d, endDate: d });
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors text-sm"
              >
                勤怠CSV出力
              </button>
              <button
                onClick={() => {
                  const d = moment(selectedDate).format('YYYY-MM-DD');
                  exportCsv({ type: 'reports', startDate: d, endDate: d });
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm"
              >
                日報CSV出力
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : attendanceRecords.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              この日付の打刻データがありません
            </div>
          ) : (
            <AttendanceTable
              records={attendanceRecords}
              onViewDetail={fetchAttendanceDetail}
            />
          )}
        </>
      ) : (
        <>
          {/* 月次ビュー */}
          <MonthlySelector
            selectedMonth={selectedMonth}
            selectedUserId={selectedUserId}
            users={users}
            onMonthChange={setSelectedMonth}
            onUserChange={setSelectedUserId}
            onFetch={() => fetchMonthlyAttendance(selectedMonth, selectedUserId)}
          />

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedUserId && users.find(u => u.id === parseInt(selectedUserId))?.name}
              {' - '}
              {moment(selectedMonth).format('YYYY年M月')}の勤怠データ
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const start = moment(selectedMonth).startOf('month').format('YYYY-MM-DD');
                  const end = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');
                  exportCsv({ type: 'attendance', startDate: start, endDate: end, userId: selectedUserId || undefined });
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors text-sm"
              >
                勤怠CSV出力
              </button>
              <button
                onClick={() => {
                  const start = moment(selectedMonth).startOf('month').format('YYYY-MM-DD');
                  const end = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');
                  exportCsv({ type: 'reports', startDate: start, endDate: end, userId: selectedUserId || undefined });
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 transition-colors text-sm"
              >
                日報CSV出力
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : !selectedUserId ? (
            <div className="text-center py-10 text-gray-500">
              ユーザーを選択してください
            </div>
          ) : monthlyRecords.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              この期間の打刻データがありません
            </div>
          ) : (
            <MonthlyCalendarView
              month={selectedMonth}
              records={monthlyRecords}
              onViewDetail={fetchAttendanceDetail}
            />
          )}
        </>
      )}
    </div>
  );
}

// ヘッダー
const Header = () => (
  <div className="mb-6">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">打刻管理（管理者）</h1>
    <p className="text-gray-600 text-sm md:text-base mt-2">従業員の打刻修正・代理打刻・確認を管理します</p>
  </div>
);

// メッセージ表示
const MessageDisplay = ({ error, success }) => (
  <>
    {error && (
      <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-xs md:text-sm text-red-600">{error}</p>
      </div>
    )}
    {success && (
      <div className="mb-4 p-3 md:p-4 bg-green-50 border border-green-200 rounded-md">
        <p className="text-xs md:text-sm text-green-600">{success}</p>
      </div>
    )}
  </>
);

// ローディング
const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-20 text-gray-500">
    読み込み中...
  </div>
);

// 日付選択
const DateSelector = ({ selectedDate, onDateChange, onFetch }) => (
  <div className="mb-6 flex flex-col md:flex-row items-start md:items-center gap-3">
    <input
      type="date"
      value={selectedDate}
      onChange={(e) => {
        onDateChange(e.target.value);
      }}
      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
    />
    <button
      onClick={() => onFetch(selectedDate)}
      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm"
    >
      検索
    </button>
    <button
      onClick={() => {
        const today = moment().format('YYYY-MM-DD');
        onDateChange(today);
        onFetch(today);
      }}
      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
    >
      今日
    </button>
  </div>
);

// 月次選択
const MonthlySelector = ({ selectedMonth, selectedUserId, users, onMonthChange, onUserChange, onFetch }) => {
  const userArray = Array.isArray(users) ? users : [];
  
  return (
    <div className="mb-6 space-y-3">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">対象月</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => onMonthChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">従業員</label>
          <select
            value={selectedUserId}
            onChange={(e) => onUserChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">選択してください</option>
            {userArray.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        <div className="self-end">
          <button
            onClick={onFetch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
          >
            検索
          </button>
        </div>
      </div>
    </div>
  );
};

// 月次カレンダービュー
const MonthlyCalendarView = ({ month, records, onViewDetail }) => {
  const recordsByDate = {};
  records.forEach((record) => {
    recordsByDate[record.work_date] = record;
  });

  const monthStart = moment(month).startOf('month');
  const monthEnd = moment(month).endOf('month');
  const startDate = monthStart.clone().startOf('week');
  const endDate = monthEnd.clone().endOf('week');

  const weeks = [];
  let currentWeek = [];
  let day = startDate.clone();

  while (day.isSameOrBefore(endDate)) {
    currentWeek.push(day.clone());
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    day.add(1, 'day');
  }

  const isCurrentMonth = (date) => date.format('YYYY-MM') === month;

  return (
    <div className="space-y-6">
      {/* カレンダー表示 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-100 border-b">
          {['日', '月', '火', '水', '木', '金', '土'].map((dayName, idx) => (
            <div
              key={idx}
              className={`px-2 py-3 text-center text-xs md:text-sm font-semibold ${
                idx === 0 ? 'text-red-600' : idx === 6 ? 'text-blue-600' : 'text-gray-700'
              }`}
            >
              {dayName}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {weeks.map((week, weekIdx) =>
            week.map((day, dayIdx) => {
              const dateStr = day.format('YYYY-MM-DD');
              const record = recordsByDate[dateStr];
              const inMonth = isCurrentMonth(day);
              const isToday = day.isSame(moment(), 'day');
              const isSunday = dayIdx === 0;
              const isSaturday = dayIdx === 6;

              return (
                <div
                  key={`${weekIdx}-${dayIdx}`}
                  className={`min-h-[80px] md:min-h-[100px] border-b border-r p-2 ${
                    !inMonth ? 'bg-gray-50' : 'bg-white'
                  } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                >
                  <div
                    className={`text-xs md:text-sm font-medium mb-1 ${
                      !inMonth
                        ? 'text-gray-400'
                        : isSunday
                        ? 'text-red-600'
                        : isSaturday
                        ? 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {day.format('D')}
                  </div>
                  {inMonth && record && (
                    <div className="space-y-1">
                      <div className="text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">出:</span>
                          <span className="font-medium">{record.clock_in ? moment(record.clock_in, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">退:</span>
                          <span className="font-medium">{record.clock_out ? moment(record.clock_out, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}</span>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                            record.clock_in && record.clock_out
                              ? 'bg-green-100 text-green-700'
                              : record.clock_in
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {record.clock_in && record.clock_out
                            ? '完'
                            : record.clock_in
                            ? '出'
                            : '-'}
                        </span>
                      </div>
                      <button
                        onClick={() => onViewDetail(record.id)}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        詳細
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 一覧表示 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <h3 className="text-lg font-semibold text-gray-800 px-4 py-3 bg-gray-50 border-b">
          勤怠一覧
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  日付
                </th>
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  出勤時刻
                </th>
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  退勤時刻
                </th>
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  勤務時間
                </th>
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  ステータス
                </th>
                <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
                  アクション
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => {
                const workDate = moment(record.work_date);
                const dayOfWeek = workDate.format('ddd');
                const isSunday = workDate.day() === 0;
                const isSaturday = workDate.day() === 6;

                return (
                  <tr key={record.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 text-xs md:text-sm">
                      <div
                        className={`font-medium ${
                          isSunday ? 'text-red-600' : isSaturday ? 'text-blue-600' : 'text-gray-800'
                        }`}
                      >
                        {workDate.format('M/D')} ({dayOfWeek})
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs md:text-sm text-gray-800">
                      {record.clock_in ? moment(record.clock_in, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}
                    </td>
                    <td className="px-3 py-3 text-xs md:text-sm text-gray-800">
                      {record.clock_out ? moment(record.clock_out, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}
                    </td>
                    <td className="px-3 py-3 text-xs md:text-sm text-gray-800">
                      {record.work_hours ? `${record.work_hours}h` : '-'}
                    </td>
                    <td className="px-3 py-3 text-xs md:text-sm">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          record.clock_in && record.clock_out
                            ? 'bg-green-100 text-green-700'
                            : record.clock_in
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {record.clock_in && record.clock_out
                          ? '退勤済'
                          : record.clock_in
                          ? '出勤中'
                          : '未打刻'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs md:text-sm">
                      <button
                        onClick={() => onViewDetail(record.id)}
                        className="px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs"
                      >
                        詳細
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 統計情報 */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">月間統計</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">出勤日数</div>
            <div className="text-2xl font-bold text-blue-600">
              {records.filter((r) => r.clock_in).length}日
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">退勤完了</div>
            <div className="text-2xl font-bold text-green-600">
              {records.filter((r) => r.clock_in && r.clock_out).length}日
            </div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">総勤務時間</div>
            <div className="text-2xl font-bold text-yellow-600">
              {records
                .reduce((sum, r) => sum + (parseFloat(r.work_hours) || 0), 0)
                .toFixed(1)}h
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">総残業時間</div>
            <div className="text-2xl font-bold text-purple-600">
              {records
                .reduce((sum, r) => sum + (parseFloat(r.overtime_hours) || 0), 0)
                .toFixed(1)}h
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 打刻テーブル
const AttendanceTable = ({
  records,
  onViewDetail
}) => {
  // records が配列でない場合の保護
  const recordArray = Array.isArray(records) ? records : [];

  return (
    <div className="overflow-x-auto rounded-lg shadow-sm md:shadow-md">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-3 py-3 md:px-4 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
              従業員名
            </th>
            <th className="px-3 py-3 md:px-4 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
              打刻時刻
            </th>
            <th className="px-3 py-3 md:px-4 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
              退勤時刻
            </th>
            <th className="px-3 py-3 md:px-4 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
              ステータス
            </th>
            <th className="px-3 py-3 md:px-4 text-left text-xs md:text-sm font-semibold text-gray-700 border-b">
              アクション
            </th>
          </tr>
        </thead>
        <tbody>
          {recordArray.map((record) => (
          <tr key={record.id} className="border-b hover:bg-gray-50 transition-colors">
            <td className="px-3 py-3 md:px-4 text-xs md:text-sm text-gray-800">
              {record.name}
            </td>
            <td className="px-3 py-3 md:px-4 text-xs md:text-sm text-gray-800">
              {record.clock_in ? moment(record.clock_in, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}
            </td>
            <td className="px-3 py-3 md:px-4 text-xs md:text-sm text-gray-800">
              {record.clock_out ? moment(record.clock_out, ['HH:mm:ss', 'HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('HH:mm') : '-'}
            </td>
            <td className="px-3 py-3 md:px-4 text-xs md:text-sm">
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                  record.clock_in && record.clock_out
                    ? 'bg-green-100 text-green-700'
                    : record.clock_in && !record.clock_out
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {record.clock_in && record.clock_out ? '退勤済' : record.clock_in && !record.clock_out ? '出勤中' : '未打刻'}
              </span>
            </td>
            <td className="px-3 py-3 md:px-4 text-xs md:text-sm space-x-2">
              <button
                onClick={() => onViewDetail(record.id)}
                className="px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-xs"
              >
                詳細
              </button>
            </td>
          </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// 詳細情報モーダル
const AttendanceDetailModal = ({
  record,
  images,
  stamps,
  stampLoading,
  onCreateStamp,
  onUpdateStamp,
  onDeleteStamp,
  onClose,
  loading
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [newStamp, setNewStamp] = useState({ type: '出勤', time: '', memo: '' });
  const [editingStampId, setEditingStampId] = useState(null);
  const [editStampValues, setEditStampValues] = useState({});

  if (!record) return null;

  const selectedImage = images[selectedImageIndex];
  const stampList = Array.isArray(stamps) ? stamps : [];

  const toTimeValue = (value) => {
    if (!value) return '';
    return moment(value).format('HH:mm');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-bold text-gray-900">打刻詳細</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* 基本情報 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">従業員名</label>
              <p className="text-sm md:text-base text-gray-900 font-medium">{record.name}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">勤務日</label>
              <p className="text-sm md:text-base text-gray-900">{record.work_date}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">打刻時刻</label>
              <p className="text-sm md:text-base text-gray-900">{record.clock_in || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">退勤時刻</label>
              <p className="text-sm md:text-base text-gray-900">{record.clock_out || '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">勤務時間</label>
              <p className="text-sm md:text-base text-gray-900">{record.work_hours ? `${record.work_hours}時間` : '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">残業時間</label>
              <p className="text-sm md:text-base text-gray-900">{record.overtime_hours ? `${record.overtime_hours}時間` : '-'}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">ステータス</label>
              <span
                className={`inline-block px-3 py-1 rounded text-xs font-medium ${
                  record.status === 'present'
                    ? 'bg-green-100 text-green-700'
                    : record.status === 'absent'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {record.status === 'present' ? '出勤' : record.status === 'absent' ? '欠勤' : '不明'}
              </span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">休憩時間</label>
              <p className="text-sm md:text-base text-gray-900">{record.total_break_minutes ? `${record.total_break_minutes}分` : '-'}</p>
            </div>
            {record.first_break_start && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最初の休憩開始</label>
                <p className="text-sm md:text-base text-gray-900">{record.first_break_start}</p>
              </div>
            )}
            {record.last_break_end && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">最後の休憩終了</label>
                <p className="text-sm md:text-base text-gray-900">{record.last_break_end}</p>
              </div>
            )}
          </div>

          {/* 打刻履歴 */}
          <div className="border-t pt-6">
            <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-3">打刻履歴</h4>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">種別</label>
                <select
                  value={newStamp.type}
                  onChange={(e) => setNewStamp({ ...newStamp, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="出勤">出勤</option>
                  <option value="退勤">退勤</option>
                  <option value="休憩開始">休憩開始</option>
                  <option value="休憩終了">休憩終了</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">打刻時刻</label>
                <input
                  type="time"
                  value={newStamp.time}
                  onChange={(e) => setNewStamp({ ...newStamp, time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">メモ</label>
                <input
                  type="text"
                  value={newStamp.memo}
                  onChange={(e) => setNewStamp({ ...newStamp, memo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end mb-6">
              <button
                onClick={() => {
                  if (!newStamp.time) return;
                  onCreateStamp({
                    user_id: record.user_id,
                    work_date: record.work_date,
                    type: newStamp.type,
                    time: newStamp.time,
                    memo: newStamp.memo
                  });
                  setNewStamp({ type: '出勤', time: '', memo: '' });
                }}
                disabled={stampLoading || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
              >
                追加
              </button>
            </div>

            {stampLoading ? (
              <div className="text-sm text-gray-500">打刻履歴を読み込み中...</div>
            ) : stampList.length === 0 ? (
              <div className="text-sm text-gray-500">打刻履歴がありません</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">種別</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">打刻時刻</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">メモ</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stampList.map((stamp) => {
                      const isEditing = editingStampId === stamp.id;
                      const editValue = editStampValues[stamp.id] || {};

                      return (
                        <tr key={stamp.id} className="border-b">
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <select
                                value={editValue.type ?? stamp.type}
                                onChange={(e) => setEditStampValues({
                                  ...editStampValues,
                                  [stamp.id]: { ...editValue, type: e.target.value }
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="出勤">出勤</option>
                                <option value="退勤">退勤</option>
                                <option value="休憩開始">休憩開始</option>
                                <option value="休憩終了">休憩終了</option>
                              </select>
                            ) : (
                              stamp.type
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <input
                                type="time"
                                value={editValue.time ?? toTimeValue(stamp.timestamp)}
                                onChange={(e) => setEditStampValues({
                                  ...editStampValues,
                                  [stamp.id]: { ...editValue, time: e.target.value }
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              moment(stamp.timestamp).format('HH:mm')
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editValue.memo ?? (stamp.memo || '')}
                                onChange={(e) => setEditStampValues({
                                  ...editStampValues,
                                  [stamp.id]: { ...editValue, memo: e.target.value }
                                })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            ) : (
                              stamp.memo || '-'
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {isEditing ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    const updated = editStampValues[stamp.id] || {};
                                    onUpdateStamp({
                                      id: stamp.id,
                                      user_id: record.user_id,
                                      work_date: record.work_date,
                                      type: updated.type ?? stamp.type,
                                      time: updated.time ?? toTimeValue(stamp.timestamp),
                                      memo: updated.memo ?? stamp.memo
                                    });
                                    setEditingStampId(null);
                                  }}
                                  disabled={stampLoading || loading}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
                                >
                                  保存
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingStampId(null);
                                    setEditStampValues({
                                      ...editStampValues,
                                      [stamp.id]: undefined
                                    });
                                  }}
                                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs"
                                >
                                  キャンセル
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setEditingStampId(stamp.id);
                                    setEditStampValues({
                                      ...editStampValues,
                                      [stamp.id]: {
                                        type: stamp.type,
                                        time: toTimeValue(stamp.timestamp),
                                        memo: stamp.memo || ''
                                      }
                                    });
                                  }}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
                                >
                                  編集
                                </button>
                                <button
                                  onClick={() => {
                                    onDeleteStamp({
                                      id: stamp.id,
                                      user_id: record.user_id,
                                      work_date: record.work_date
                                    });
                                  }}
                                  disabled={stampLoading || loading}
                                  className="px-2 py-1 bg-red-600 text-white rounded text-xs"
                                >
                                  削除
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 画像ギャラリー */}
          {images.length > 0 && (
            <div className="border-t pt-6">
              <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">提出画像</h4>
              <div className="space-y-4">
                {/* メイン画像 */}
                {selectedImage && (
                  <div className="bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src={selectedImage.file_path}
                      alt="Attendance"
                      className="w-full h-auto max-h-96 object-contain"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22300%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22400%22 height=%22300%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 font-size=%2216%22 fill=%22%239ca3af%22 text-anchor=%22middle%22 dominant-baseline=%22middle%22%3E画像を読み込めません%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                )}

                {/* 画像情報 */}
                {selectedImage && (
                  <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded">
                    <p><strong>形式:</strong> {selectedImage.mime_type}</p>
                    <p><strong>タイムスタンプ:</strong> {selectedImage.created_at}</p>
                  </div>
                )}

                {/* サムネイル一覧 */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded border-2 transition-colors ${
                          index === selectedImageIndex
                            ? 'border-blue-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={`${img.file_path}`}
                          alt={`Thumbnail ${index}`}
                          className="w-full h-full object-cover rounded"
                          onError={(e) => {
                            e.target.style.background = '#f3f4f6';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 md:px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

// 代理打刻モーダル
const ProxyAttendanceModal = ({ users, proxyForm, onFormChange, onSubmit, onClose, loading }) => {
  // usersが配列でない場合の保護
  const userArray = Array.isArray(users) ? users : [];
  const breaks = Array.isArray(proxyForm.breaks) ? proxyForm.breaks : [];

  const updateBreak = (index, field, value) => {
    const updated = breaks.map((entry, idx) => {
      if (idx !== index) return entry;
      return { ...entry, [field]: value };
    });
    onFormChange({ ...proxyForm, breaks: updated });
  };

  const addBreak = () => {
    onFormChange({ ...proxyForm, breaks: [...breaks, { start: '', end: '' }] });
  };

  const removeBreak = (index) => {
    const updated = breaks.filter((_, idx) => idx !== index);
    onFormChange({ ...proxyForm, breaks: updated });
  };

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-4 md:p-6">
      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4">代理打刻</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
            従業員
          </label>
          <select
            value={proxyForm.user_id}
            onChange={(e) => onFormChange({ ...proxyForm, user_id: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">選択してください</option>
            {userArray.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
            打刻時刻（オプション）
          </label>
          <input
            type="time"
            value={proxyForm.clock_in}
            onChange={(e) => onFormChange({ ...proxyForm, clock_in: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>

        <div>
          <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
            退勤時刻（オプション）
          </label>
          <input
            type="time"
            value={proxyForm.clock_out}
            onChange={(e) => onFormChange({ ...proxyForm, clock_out: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs md:text-sm font-medium text-gray-700">
              休憩（複数可）
            </label>
            <button
              type="button"
              onClick={addBreak}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              + 追加
            </button>
          </div>
          {breaks.length === 0 ? (
            <p className="text-xs text-gray-500">休憩を追加する場合は「+ 追加」を押してください</p>
          ) : (
            <div className="space-y-2">
              {breaks.map((entry, index) => (
                <div key={`break-${index}`} className="flex items-center gap-2">
                  <input
                    type="time"
                    value={entry.start || ''}
                    onChange={(e) => updateBreak(index, 'start', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <span className="text-xs text-gray-500">〜</span>
                  <input
                    type="time"
                    value={entry.end || ''}
                    onChange={(e) => updateBreak(index, 'end', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeBreak(index)}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    削除
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mt-6">
        <button
          onClick={onSubmit}
          disabled={loading}
          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
        >
          登録
        </button>
        <button
          onClick={onClose}
          disabled={loading}
          className="flex-1 px-3 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm disabled:opacity-50"
        >
          キャンセル
        </button>
      </div>
    </div>
  </div>
  );
};

export default Attendance;
